import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input, Badge } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import {
  aiSettingsApi, type AiProvider, type AiProviderInput, type AiProviderType, type ProviderTestResult,
} from '../../lib/aiSettings';

const TYPES: AiProviderType[] = ['openai_compatible', 'bedrock', 'vertex', 'deepl'];
const DEFAULT_REGION: Partial<Record<AiProviderType, string>> = { bedrock: 'eu-central-1', vertex: 'europe-west3' };
const MODEL_PLACEHOLDER: Record<AiProviderType, string> = {
  openai_compatible: 'qwen2.5:14b · mistral-small-latest',
  bedrock: 'eu.anthropic.claude-… (EU-Inference-Profile)',
  vertex: 'gemini-2.5-flash · claude-… · mistral-…',
  deepl: '',
};
const isEu = (r: string) => r.startsWith('eu-') || r.startsWith('europe-');

type Draft = AiProviderInput & { type: AiProviderType };

export function AiProvidersSettings({ dark, providers, onChanged }: {
  dark: boolean; providers: AiProvider[]; onChanged: () => Promise<void> | void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [editing, setEditing] = useState<AiProvider | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>({ type: 'openai_compatible' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<number, ProviderTestResult | 'running'>>({});

  const startNew = () => { setEditing('new'); setDraft({ type: 'openai_compatible', is_active: true, timeout_seconds: 60 }); setError(null); };
  const startEdit = (p: AiProvider) => {
    setEditing(p);
    setDraft({ type: p.type, name: p.name, base_url: p.base_url, region: p.region, project_id: p.project_id,
      model: p.model, timeout_seconds: p.timeout_seconds, is_active: p.is_active });
    setError(null);
  };
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft(d => ({ ...d, [k]: v }));
  const setType = (type: AiProviderType) => setDraft(d => ({ ...d, type, region: DEFAULT_REGION[type] ?? null }));

  const save = async () => {
    setSaving(true); setError(null);
    const payload: AiProviderInput = { ...draft };
    if (!payload.api_key) delete payload.api_key;
    if (!payload.secret) delete payload.secret;
    try {
      if (editing === 'new') await aiSettingsApi.createProvider(payload);
      else if (editing) await aiSettingsApi.updateProvider(editing.id, payload);
      setEditing(null);
      await onChanged();
    } catch {
      setError(tr('settings.api.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (p: AiProvider) => {
    setTests(s => ({ ...s, [p.id]: 'running' }));
    try {
      const r = await aiSettingsApi.testProvider(p.id);
      setTests(s => ({ ...s, [p.id]: r }));
    } catch {
      setTests(s => ({ ...s, [p.id]: { ok: false, text: null, duration_ms: 0, error: tr('settings.api.save_error') } }));
    }
  };

  const remove = async (p: AiProvider) => {
    if (!window.confirm(tr('settings.api.providers.delete', { name: p.name }) + '?')) return;
    await aiSettingsApi.deleteProvider(p.id);
    await onChanged();
  };

  const keepHint = (hint: string | null) => (editing !== 'new' && hint ? tr('settings.api.form.keep_secret', { hint }) : undefined);
  const current = editing && editing !== 'new' ? editing : null;
  const type = draft.type;

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle dark={dark} title={tr('settings.api.providers.title')} subtitle={tr('settings.api.providers.subtitle')} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {providers.map(p => {
          const test = tests[p.id];
          return (
            <div key={p.id} style={{ border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ color: t.heading }}>{p.name}</strong>
                <span style={{ fontSize: 12, color: t.muted }}>{tr(`settings.api.form.type_${p.type}`)}</span>
                {p.model && <span style={{ fontSize: 12, color: t.muted }}>· {p.model}</span>}
                {p.region && <span style={{ fontSize: 12, color: t.muted }}>· {p.region}</span>}
                {(p.api_key_masked || p.secret_hint) && (
                  <span style={{ fontSize: 12, color: t.muted }}>· {[p.api_key_masked, p.secret_hint].filter(Boolean).join(' · ')}</span>
                )}
                {!p.is_active && <Badge dark={dark} tone="neutral">{tr('settings.api.providers.inactive')}</Badge>}
                {!p.eu_region && <Badge dark={dark} tone="warning">{tr('settings.api.providers.non_eu')}</Badge>}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <Button size="sm" variant="secondary" dark={dark} onClick={() => runTest(p)}
                    style={{}} disabled={test === 'running'}>
                    <span aria-label={tr('settings.api.providers.test', { name: p.name })}>⚡</span>
                  </Button>
                  <Button size="sm" variant="secondary" dark={dark} onClick={() => startEdit(p)}>
                    <span aria-label={tr('settings.api.providers.edit', { name: p.name })}>✎</span>
                  </Button>
                  <Button size="sm" variant="danger" dark={dark} onClick={() => remove(p)}>
                    <span aria-label={tr('settings.api.providers.delete', { name: p.name })}>🗑</span>
                  </Button>
                </div>
              </div>
              {test && test !== 'running' && (
                <div style={{ fontSize: 12, marginTop: 6, color: test.ok ? '#027a48' : '#b42318' }}>
                  {test.ok
                    ? tr('settings.api.providers.test_ok', { ms: test.duration_ms, text: test.text ?? '' })
                    : tr('settings.api.providers.test_failed', { error: test.error ?? '' })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing === null ? (
        <div style={{ marginTop: 14 }}>
          <Button dark={dark} onClick={startNew}>{tr('settings.api.providers.add')}</Button>
        </div>
      ) : (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <Field label={tr('settings.api.form.name')}>
            <Input dark={dark} aria-label={tr('settings.api.form.name')} value={draft.name ?? ''} onChange={e => set('name', e.target.value)} />
          </Field>
          <Field label={tr('settings.api.form.type')}>
            <select aria-label={tr('settings.api.form.type')} value={type} disabled={current !== null}
              onChange={e => setType(e.target.value as AiProviderType)}
              style={{ padding: '9px 12px', borderRadius: 9, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text }}>
              {TYPES.map(ty => <option key={ty} value={ty}>{tr(`settings.api.form.type_${ty}`)}</option>)}
            </select>
          </Field>
          {(type === 'openai_compatible' || type === 'deepl') && (
            <Field label={tr('settings.api.form.base_url')}>
              <Input dark={dark} aria-label={tr('settings.api.form.base_url')} value={draft.base_url ?? ''}
                placeholder={type === 'deepl' ? 'https://api.deepl.com' : 'http://ollama.intern:11434/v1'}
                onChange={e => set('base_url', e.target.value)} />
            </Field>
          )}
          {(type === 'bedrock' || type === 'vertex') && (
            <Field label={tr('settings.api.form.region')}
              error={draft.region && !isEu(draft.region) ? tr('settings.api.form.eu_warning') : undefined}>
              <Input dark={dark} aria-label={tr('settings.api.form.region')} value={draft.region ?? ''} onChange={e => set('region', e.target.value)} />
            </Field>
          )}
          {type === 'vertex' && (
            <Field label={tr('settings.api.form.project_id')}>
              <Input dark={dark} aria-label={tr('settings.api.form.project_id')} value={draft.project_id ?? ''} onChange={e => set('project_id', e.target.value)} />
            </Field>
          )}
          {type !== 'vertex' && (
            <Field label={type === 'bedrock' ? tr('settings.api.form.access_key') : tr('settings.api.form.api_key')}
              hint={keepHint(current?.api_key_masked ?? null)}>
              <Input dark={dark} type="password" autoComplete="off"
                aria-label={type === 'bedrock' ? tr('settings.api.form.access_key') : tr('settings.api.form.api_key')}
                value={draft.api_key ?? ''} onChange={e => set('api_key', e.target.value)} />
            </Field>
          )}
          {(type === 'bedrock' || type === 'vertex') && (
            <Field label={type === 'bedrock' ? tr('settings.api.form.secret_key') : tr('settings.api.form.service_account')}
              hint={keepHint(current?.secret_hint ?? null)}>
              {type === 'vertex' ? (
                <textarea aria-label={tr('settings.api.form.service_account')} rows={4} value={draft.secret ?? ''}
                  onChange={e => set('secret', e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: 12, padding: 8, borderRadius: 9, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text }} />
              ) : (
                <Input dark={dark} type="password" autoComplete="off" aria-label={tr('settings.api.form.secret_key')}
                  value={draft.secret ?? ''} onChange={e => set('secret', e.target.value)} />
              )}
            </Field>
          )}
          {type !== 'deepl' && (
            <Field label={tr('settings.api.form.model')}>
              <Input dark={dark} aria-label={tr('settings.api.form.model')} value={draft.model ?? ''}
                placeholder={MODEL_PLACEHOLDER[type]} onChange={e => set('model', e.target.value)} />
            </Field>
          )}
          <Field label={tr('settings.api.form.timeout')}>
            <Input dark={dark} type="number" aria-label={tr('settings.api.form.timeout')} value={String(draft.timeout_seconds ?? 60)}
              onChange={e => set('timeout_seconds', Number(e.target.value))} />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: t.text }}>
            <input type="checkbox" checked={draft.is_active ?? true} onChange={e => set('is_active', e.target.checked)} />
            {tr('settings.api.form.active')}
          </label>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button dark={dark} onClick={save} disabled={saving}>{tr('settings.api.form.save')}</Button>
            <Button dark={dark} variant="secondary" onClick={() => setEditing(null)}>{tr('settings.api.form.cancel')}</Button>
            {error && <span style={{ color: '#b42318', fontSize: 12 }}>{error}</span>}
          </div>
        </div>
      )}
    </Card>
  );
}
